import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  COMPANY_KIND_LABELS,
  COMPANY_KINDS,
  TRADES,
  type CompanyDetailDto,
  type CompanyKind,
  type DirectoryPersonDto,
  type Trade,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { DuplicateHint, errMsg, useDirectoryRefresh, useDuplicates } from "./DirectoryDialogs";

/**
 * Snel toevoegen aan het adresboek vanuit een kiezer (docs/UX-RICHTLIJNEN.md §6): alleen de gegevens die je
 * meteen nodig hebt. De rest vul je later aan in het Adresboek. Eén plek, zodat elke kiezer hetzelfde werkt.
 */
function useAfterAdd() {
  const qc = useQueryClient();
  const refresh = useDirectoryRefresh();
  return () => Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["directory-pick"] })]);
}

/** Nieuw bedrijf (standaard een leverancier) met eventueel meteen een contactpersoon. */
export function QuickCompanyDialog({
  initialName = "",
  kind = "supplier",
  chooseKind = false,
  onClose,
  onSaved,
}: {
  initialName?: string;
  kind?: CompanyKind;
  /** Soort laten kiezen (gebouwcontacten); anders vast `kind`. */
  chooseKind?: boolean;
  onClose: () => void;
  /** Het nieuwe bedrijf, of het bestaande als je "Die gebruiken" kiest bij een dubbele. */
  onSaved: (companyId: string, personId: string | null) => void;
}) {
  const toast = useToast();
  const afterAdd = useAfterAdd();
  const [name, setName] = useState(initialName);
  const [kinds, setKinds] = useState<CompanyKind[]>([kind]);
  const [trade, setTrade] = useState<Trade | "">("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [person, setPerson] = useState("");
  const [personPhone, setPersonPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dups = useDuplicates("company", { name, email, phone });
  const noun = !chooseKind && kind === "supplier" ? "leverancier" : "bedrijf";

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!name.trim() || !kinds.length) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api<CompanyDetailDto>("/companies", {
        method: "POST",
        body: { id: crypto.randomUUID(), name, kinds, trades: trade ? [trade] : [], phone, email },
      });
      let personId: string | null = null;
      if (person.trim()) {
        const p = await api<DirectoryPersonDto>("/people", {
          method: "POST",
          body: { id: crypto.randomUUID(), name: person, companyId: c.id, mobile: personPhone },
        });
        personId = p.id;
      }
      await afterAdd();
      toast(`${c.name} toegevoegd aan het adresboek`);
      onSaved(c.id, personId);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Nieuwe ${noun}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || !name.trim() || !kinds.length}
          >
            Toevoegen
          </button>
        </>
      }
    >
      <form onSubmit={save} className="dir-form">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Naam *</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoComplete="organization"
            placeholder="bijv. Liftservice Zuid"
          />
        </label>
        <DuplicateHint hits={dups?.filter((d) => d.type === "company")} onUse={(d) => onSaved(d.id, null)} />
        {chooseKind && (
          <fieldset className="field dir-checks">
            <legend className="label">Soort *</legend>
            {COMPANY_KINDS.map((k) => (
              <label key={k} className="dir-check">
                <input
                  type="checkbox"
                  checked={kinds.includes(k)}
                  onChange={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])}
                />{" "}
                {COMPANY_KIND_LABELS[k]}
              </label>
            ))}
          </fieldset>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Vakgebied</span>
            <select value={trade} onChange={(e) => setTrade(e.target.value as Trade | "")}>
              <option value="">Algemeen</option>
              {TRADES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Telefoon</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" />
          </label>
        </div>
        <label className="field">
          <span className="label">E-mailadres</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="off" />
        </label>
        <div className="grid2">
          <label className="field">
            <span className="label">Contactpersoon</span>
            <input value={person} onChange={(e) => setPerson(e.target.value)} autoComplete="off" placeholder="naam" />
          </label>
          <label className="field">
            <span className="label">Mobiel contactpersoon</span>
            <input
              value={personPhone}
              onChange={(e) => setPersonPhone(e.target.value)}
              inputMode="tel"
              autoComplete="off"
              disabled={!person.trim()}
            />
          </label>
        </div>
        <p className="hint">Adres, KvK-nummer en openingstijden vul je later aan in het Adresboek.</p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Nieuwe contactpersoon bij een bedrijf uit het adresboek. */
export function QuickPersonDialog({
  initialName = "",
  companyId,
  companyName,
  onClose,
  onSaved,
}: {
  initialName?: string;
  companyId: string;
  companyName: string;
  onClose: () => void;
  onSaved: (personId: string) => void;
}) {
  const toast = useToast();
  const afterAdd = useAfterAdd();
  const [name, setName] = useState(initialName);
  const [jobTitle, setJobTitle] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dups = useDuplicates("person", { name, email, phone: mobile });

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const p = await api<DirectoryPersonDto>("/people", {
        method: "POST",
        body: { id: crypto.randomUUID(), name, companyId, jobTitle, mobile, email },
      });
      await afterAdd();
      toast(`${p.name} toegevoegd aan ${companyName}`);
      onSaved(p.id);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Nieuwe contactpersoon bij ${companyName}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !name.trim()}>
            Toevoegen
          </button>
        </>
      }
    >
      <form onSubmit={save} className="dir-form">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Naam *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">Functie</span>
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="bijv. planner, servicemonteur"
            />
          </label>
        </div>
        <DuplicateHint hits={dups?.filter((d) => d.type === "person")} onUse={(d) => onSaved(d.id)} />
        <div className="grid2">
          <label className="field">
            <span className="label">Mobiel</span>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)} inputMode="tel" autoComplete="off" />
          </label>
          <label className="field">
            <span className="label">E-mailadres</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="off" />
          </label>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
